import {
  type PrivateFormData,
  fromPrivateFieldRows,
  privateFieldsForGender,
  toPrivatePayload,
} from '@/entities/profile';
import { NODE_TYPES } from '@/entities/user';
import { useAuth } from '@/features/authentication';
import { profileApi, userApi } from '@/features/profile-management';
import {
  type PhotoStepData,
  type PracticeStepData,
  type ProfileStepData,
  type SituationStepData,
  Step1BasicInfo,
  type Step1Data,
  StepPhone,
  StepPhoto,
  StepPractice,
  StepPrivate,
  StepProfile,
  StepSituation,
  registrationApi,
} from '@/features/registration';
import { referenceApi } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import { Progress } from '@/shared/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { AxiosError } from 'axios';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Mirrors the legacy signup: 1 account → 2 phone (Firebase) → 3/4/5 public
// profile → 6 photo (mandatory) → 7 private profile → activation.
const STEP_ACCOUNT = 1;
const STEP_PHONE = 2;
const STEP_PROFILE = 3;
const STEP_SITUATION = 4;
const STEP_PRACTICE = 5;
const STEP_PHOTO = 6;
const STEP_PRIVATE = 7;
const TOTAL_STEPS = 7;

const STEP_KEYS: Record<number, string> = {
  [STEP_ACCOUNT]: 'account',
  [STEP_PHONE]: 'phone',
  [STEP_PROFILE]: 'profile',
  [STEP_SITUATION]: 'situation',
  [STEP_PRACTICE]: 'practice',
  [STEP_PHOTO]: 'photo',
  [STEP_PRIVATE]: 'private',
};

const initialAccount: Step1Data = {
  gender: 1,
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  firstname: '',
  lastname: '',
  birthday: '',
};

const initialProfile: ProfileStepData = {
  avatarId: undefined,
  description: '',
  zipCode: '',
  geoId: undefined,
  cityLabel: '',
};

const initialSituation: SituationStepData = { profession: '' };

const initialPractice: PracticeStepData = { spokenLanguages: [], lifePersonality: [] };

const initialPhoto: PhotoStepData = { photo: null, croppedPhotoBlob: null, alreadyUploaded: false };

function apiErrorMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  return axiosError.response?.data?.message || axiosError.response?.data?.error || fallback;
}

export default function RegisterPage() {
  const { t } = useTranslate();
  const { user, isLoading: isAuthLoading, register, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(STEP_ACCOUNT);
  const [account, setAccount] = useState<Step1Data>(initialAccount);
  const [verifiedPhone, setVerifiedPhone] = useState<string | undefined>(undefined);
  const [profile, setProfile] = useState<ProfileStepData>(initialProfile);
  const [situation, setSituation] = useState<SituationStepData>(initialSituation);
  const [practice, setPractice] = useState<PracticeStepData>(initialPractice);
  const [photo, setPhoto] = useState<PhotoStepData>(initialPhoto);
  const [privateData, setPrivateData] = useState<PrivateFormData>({});
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const resumedRef = useRef(false);

  const gender = user?.gender ?? account.gender;
  const isPending = user?.nodeTypeId === NODE_TYPES.PENDING_VALIDATION;

  const { data: nationalities = [] } = useQuery({
    queryKey: ['references', 'nationalities'],
    queryFn: referenceApi.listNationalities,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: currentStep >= STEP_PRACTICE,
  });

  const setStepValid = useCallback((step: number, isValid: boolean) => {
    setStepValidation((prev) => (prev[step] === isValid ? prev : { ...prev, [step]: isValid }));
  }, []);

  // Resume an unfinished signup (legacy keeps the step in session; here the
  // API tells us what is missing and the profile is reloaded to prefill).
  useEffect(() => {
    if (isAuthLoading || !user || !isPending || resumedRef.current) return;
    resumedRef.current = true;
    setIsResuming(true);
    (async () => {
      try {
        const status = await registrationApi.getStatus();
        if (!status.isPhoneVerified) {
          setCurrentStep(STEP_PHONE);
          return;
        }
        setVerifiedPhone(status.phoneNumber);
        const [existing, privateRows] = await Promise.all([
          userApi.getMyProfile(user.nodeId).catch(() => null),
          profileApi.getPrivateProfile(user.nodeId).catch(() => []),
        ]);
        if (existing) {
          setProfile({
            avatarId: undefined,
            description: existing.description ?? '',
            zipCode: existing.zip ?? '',
            geoId: undefined,
            cityLabel: existing.city ?? '',
          });
          setSituation({
            studyLevel: existing.studyLevel,
            profession: existing.profession ?? '',
            nodeBusiness: existing.nodeBusiness,
            isStudyLevelMatter: existing.isStudyLevelMatter,
            maritalStatus: existing.maritalStatus,
            children: existing.children,
            livingPlace: existing.livingPlace,
            administrativeState: existing.administrativeState,
            peerPast: existing.peerPast,
            familyValues: existing.familyValues,
          });
          setPractice({
            licence: existing.licence,
            smoke: existing.smoke,
            faith: existing.faith,
            minaj: existing.minaj,
            veil: existing.veil,
            nationality: existing.nationality,
            origin: existing.origin,
            studyCountry: existing.studyCountry,
            spokenLanguages: existing.spokenLanguages?.split(',').filter(Boolean) ?? [],
            lifePersonality: existing.lifePersonality?.split(',').filter(Boolean) ?? [],
            shape: existing.shape,
            size: existing.size,
            eye: existing.eye,
            hair: existing.hair,
          });
        }
        setPrivateData(fromPrivateFieldRows(privateFieldsForGender(user.gender), privateRows));
        setPhoto((prev) => ({ ...prev, alreadyUploaded: status.hasPhoto }));
        setCurrentStep(status.hasPhoto ? STEP_PRIVATE : STEP_PROFILE);
        toast.info(t('signup.resume'));
      } catch {
        setCurrentStep(STEP_PHONE);
      } finally {
        setIsResuming(false);
      }
    })();
  }, [isAuthLoading, user, isPending, t]);

  const runStep = async (action: () => Promise<void>, fallbackMessage: string) => {
    setIsSubmitting(true);
    try {
      await action();
    } catch (error) {
      toast.error(apiErrorMessage(error, fallbackMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAccount = () =>
    runStep(async () => {
      if (account.password !== account.confirmPassword) {
        throw new Error(t('auth.passwordMismatch'));
      }
      await register({
        email: account.email,
        username: account.username,
        password: account.password,
        firstname: account.firstname,
        lastname: account.lastname,
        birthday: account.birthday,
        gender: account.gender,
      });
      resumedRef.current = true; // the account is fresh, nothing to resume
      toast.success(t('auth.accountCreated'));
      setCurrentStep(STEP_PHONE);
    }, t('auth.registrationFailed'));

  const handlePhoneVerified = (phone: string) => {
    setVerifiedPhone(phone);
    setCurrentStep(STEP_PROFILE);
  };

  const handleSaveProfile = () =>
    runStep(async () => {
      await userApi.updateProfile({
        avatarId: profile.avatarId,
        description: profile.description.trim(),
        geoId: profile.geoId,
        zipCode: profile.zipCode || undefined,
      });
      setCurrentStep(STEP_SITUATION);
    }, t('signup.saveError'));

  const handleSaveSituation = () =>
    runStep(async () => {
      await userApi.updateProfile({
        studyLevel: situation.studyLevel,
        profession: situation.profession.trim(),
        nodeBusiness: situation.nodeBusiness,
        isStudyLevelMatter: situation.isStudyLevelMatter,
        maritalStatus: situation.maritalStatus,
        children: situation.children,
        livingPlace: situation.livingPlace,
        administrativeState: situation.administrativeState,
        peerPast: situation.peerPast,
        familyValues: situation.familyValues,
      });
      setCurrentStep(STEP_PRACTICE);
    }, t('signup.saveError'));

  const handleSavePractice = () =>
    runStep(async () => {
      await userApi.updateProfile({
        licence: practice.licence,
        smoke: practice.smoke,
        faith: practice.faith,
        minaj: practice.minaj,
        veil: gender === 2 ? practice.veil : undefined,
        nationality: practice.nationality,
        origin: practice.origin,
        studyCountry: practice.studyCountry,
        spokenLanguages: practice.spokenLanguages.join(','),
        lifePersonality: practice.lifePersonality.join(',') || undefined,
        shape: practice.shape,
        size: practice.size,
        eye: practice.eye,
        hair: practice.hair,
      });
      setCurrentStep(STEP_PHOTO);
    }, t('signup.saveError'));

  const handleSavePhoto = () =>
    runStep(async () => {
      if (photo.croppedPhotoBlob) {
        await userApi.uploadPhoto(photo.croppedPhotoBlob);
        setPhoto((prev) => ({ ...prev, alreadyUploaded: true }));
      }
      setCurrentStep(STEP_PRIVATE);
    }, t('profile.photoUploadError'));

  const handleFinish = () =>
    runStep(async () => {
      const fields = privateFieldsForGender(gender);
      await userApi.updatePrivateProfile(toPrivatePayload(fields, privateData));
      await registrationApi.complete();
      await refreshUser();
      toast.success(t('signup.completed'));
      navigate('/dashboard', { replace: true });
    }, t('signup.completeError'));

  const nextHandlers: Record<number, () => Promise<void>> = {
    [STEP_ACCOUNT]: handleCreateAccount,
    [STEP_PROFILE]: handleSaveProfile,
    [STEP_SITUATION]: handleSaveSituation,
    [STEP_PRACTICE]: handleSavePractice,
    [STEP_PHOTO]: handleSavePhoto,
    [STEP_PRIVATE]: handleFinish,
  };

  // Going back never crosses the account creation nor the verified phone.
  const canGoBack = currentStep > STEP_PROFILE;
  const handleBack = () => {
    if (canGoBack) setCurrentStep((prev) => prev - 1);
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;
  const stepKey = STEP_KEYS[currentStep];

  const renderStep = () => {
    switch (currentStep) {
      case STEP_ACCOUNT:
        return (
          <Step1BasicInfo
            data={account}
            onChange={(data) => setAccount((prev) => ({ ...prev, ...data }))}
            onValidChange={(isValid) => setStepValid(STEP_ACCOUNT, isValid)}
          />
        );
      case STEP_PHONE:
        return <StepPhone verifiedPhone={verifiedPhone} onVerified={handlePhoneVerified} />;
      case STEP_PROFILE:
        return (
          <StepProfile
            data={profile}
            gender={gender}
            onChange={(data) => setProfile((prev) => ({ ...prev, ...data }))}
            onValidChange={(isValid) => setStepValid(STEP_PROFILE, isValid)}
          />
        );
      case STEP_SITUATION:
        return (
          <StepSituation
            data={situation}
            onChange={(data) => setSituation((prev) => ({ ...prev, ...data }))}
            onValidChange={(isValid) => setStepValid(STEP_SITUATION, isValid)}
          />
        );
      case STEP_PRACTICE:
        return (
          <StepPractice
            data={practice}
            gender={gender}
            nationalities={nationalities}
            onChange={(data) => setPractice((prev) => ({ ...prev, ...data }))}
            onValidChange={(isValid) => setStepValid(STEP_PRACTICE, isValid)}
          />
        );
      case STEP_PHOTO:
        return (
          <StepPhoto
            data={photo}
            onChange={(data) => setPhoto((prev) => ({ ...prev, ...data }))}
            onValidChange={(isValid) => setStepValid(STEP_PHOTO, isValid)}
          />
        );
      case STEP_PRIVATE:
        return (
          <StepPrivate
            data={privateData}
            gender={gender}
            onChange={(data) => setPrivateData((prev) => ({ ...prev, ...data }))}
            onValidChange={(isValid) => setStepValid(STEP_PRIVATE, isValid)}
          />
        );
      default:
        return null;
    }
  };

  const nextLabel = () => {
    if (currentStep === STEP_ACCOUNT) return t('signup.buttons.createAccount');
    if (currentStep === STEP_PRIVATE) return t('signup.buttons.finish');
    return t('common.next');
  };

  return (
    <Card className="w-full max-w-lg">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            {t('signup.stepOf', { current: currentStep, total: TOTAL_STEPS })}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
              <div
                key={STEP_KEYS[i + 1]}
                className={`w-2 h-2 rounded-full ${i + 1 <= currentStep ? 'bg-primary' : 'bg-muted'}`}
              />
            ))}
          </div>
        </div>
        <Progress value={progress} className="h-2" />
        <CardTitle className="text-2xl font-bold mt-4">
          {t(`signup.steps.${stepKey}.title`)}
        </CardTitle>
        <CardDescription>{t(`signup.steps.${stepKey}.description`)}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {isResuming ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          renderStep()
        )}
      </CardContent>

      <CardFooter className="flex flex-col space-y-4">
        {currentStep !== STEP_PHONE && (
          <div className="flex w-full gap-4">
            {canGoBack && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
                className="flex-1"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.back')}
              </Button>
            )}
            <Button
              type="button"
              onClick={nextHandlers[currentStep]}
              disabled={!stepValidation[currentStep] || isSubmitting || isResuming}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {currentStep === STEP_ACCOUNT
                    ? t('auth.creatingAccount')
                    : t('signup.buttons.saving')}
                </>
              ) : currentStep === STEP_PRIVATE ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  {nextLabel()}
                </>
              ) : (
                <>
                  {nextLabel()}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}

        {currentStep === STEP_ACCOUNT && (
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline">
              {t('auth.login')}
            </Link>
          </p>
        )}
      </CardFooter>
    </Card>
  );
}
